import Button from "./Button"
import { useTranslation } from "react-i18next";
import PropTypes from 'prop-types';
import Icon from "./Icon";

const defaults = {
    add: { variant: 'primary', label: t => t('addEntry')},
    view: { variant: 'default', label: t => t('view')},
    edit: { variant: 'default',  label: t => t('edit')},
    close: { variant: 'default', label: t => t('closeDialog')},
    save: { variant: 'primary', label: t => t('saveChange')},
    submit: { variant: 'success', label: t => t('submit')},
    load: { variant: 'primary', label: t => t('load')},
    delete: { variant: 'danger', label: t => t('deleteEntry')},
    ok: { variant: 'primary', icon: null, label: t => t('ok')},
    cancel: { variant: 'default', icon: null, label: t => t('cancel')},
    print: { variant: 'default', label: t => t('printDoc')},
    copy: { variant: 'default', label: t => t('copy')},
    search: { variant: 'default', label: t => t('search')},
    upload: { variant: 'default', label: t => t('uploadFile')},
    marker: { variant: 'default', label: t => null },
    filter: { variant: 'default', label: t => t('filter', { count: 1 }) },
    clear: { variant: 'danger', label: t => t('clear') },
    settings: { variant: 'default', label: t => t('settings') },
    lock: { variant: 'default', label: t => t('lock'), icon: <Icon intent="locked"></Icon> },
    unlock: { variant: 'default', label: t => t('unlock'), icon: <Icon intent="unlocked"></Icon> },
    group: { variant: 'default', label: t => t('group') },
    ungroup: { variant: 'default', label: t => t('ungroup') },
    send: { variant: 'success', label: t => t('sendMessage') },
    preview: { variant: 'default', label: t => t('preview') },
    apply: { variant: 'success', label: t => t('applyChanges') },
    previous: { variant: 'default', label: t => t('previous') },
    next: { variant: 'default', label: t => t('next') },
    repeat: { variant: 'default', label: t => t('repeat') },
    sort: { variant: 'default', label: t => t('sort') },
    select: { variant: 'default', label: t => t('select') },
    guide: { variant: 'default', label: t => t('helpGuide'), hideLabel: true, style: { backgroundColor: 'transparent', color: '#0aaeef', fontSize: '1.3em', padding: '0px' } },
    download: { variant: 'default', label: t => t('download') },
    back: { variant: 'default', label: t => t('back') },
    popout: { variant: 'link', label: t => null, style: { backgroundColor: 'transparent', color: '#0aaeef', fontSize: '1.1em', padding: 0 } },
    pin: { variant: 'link', label: t => null, style: { backgroundColor: 'transparent', color: '#0aaeef', fontSize: '1.1em', padding: 0 } },
    unpin: { variant: 'link', label: t => null, style: { backgroundColor: 'transparent', color: '#0aaeef', fontSize: '1.1em', padding: 0 } },
}

export const DefaultButton = ({variant, kind, confirmMessage, onClick, hideLabel, label, style, ...props}) => {
    const { t } = useTranslation();
    const def = defaults[kind];
    if (!def) {
        throw new Error('Unknown button variant ' + kind);
    }
    let icon = null;
    if (def.icon) {
        icon = def.icon;
    } else if (def.icon !== null) {
        icon = <Icon intent={kind}></Icon>
    }
    const hide = (hideLabel ?? def.hideLabel ?? false) === true;

    return <Button variant={variant ?? def.variant} onClick={onClick} confirmMessage={confirmMessage} title={label ?? def.label?.(t)} style={variant ? style : { ...def.style, ...style }} {...props}>{icon} {hide ? null : label ?? def.label?.(t)}</Button>
}

DefaultButton.propTypes = {
    /**
     * The visual variant of the button
     */
    variant: PropTypes.oneOf(['default','primary','success','danger','outline-light','outline-dark','outline-primary','outline-danger','outline-success']),
    /**
     * The semantic meaning of the button - selects the button from the button library
     */
    kind: PropTypes.oneOf(Object.keys(defaults)).isRequired,
    /**
     * Function that is called when the button is clicked
     */
    onClick: PropTypes.func.isRequired,
    /**
     * Set to true to suppress the text label on the button
     */
    hideLabel: PropTypes.bool,
    /**
     * A text label for the button in place of the default for the specified kind
     */
    label: PropTypes.string,
    /**
     * Confirmation message to display before carrying out the button's action
     */
    confirmMessage: PropTypes.string,
    /**
     * Style option to change rendering of the control
     */
    style: PropTypes.object,
}

export default DefaultButton;
